import { asApp } from './singletons.ts';
import { sendEv } from '../data/events.ts';
/**
 * @file slowOpNotice.ts
 *
 * A long operation holds the app busy, and a busy app with no explanation looks
 * broken. This raises a warning notification once an operation has run past a
 * threshold, offering Cancel and Dismiss.
 *
 * Nothing appears if the operation finishes first — the notice is armed on a
 * timer and `close()` disarms it. That is the whole point: fast operations stay
 * silent, only the ones the user is actually waiting on speak up.
 *
 * Used by as-configurator (a slow configure) and as-file-input (a slow import).
 * Both pair it with an `as-app-busy-request` key: start the notice where the key
 * goes on, close it wherever the key comes off — every path, including failures.
 */
export interface SlowOpNoticeOptions {
  /** How long the operation may run before the notice appears. */
  delayMs: number;
  /** Notification body. */
  defaultText: string;
  dictKey: string;
  /** Runs when the user picks Cancel. Closing the notice is handled here, so
   *  this only has to stop the actual work. */
  onCancel: () => void;
  /** Cancel button label. Defaults to the generic "Cancel". */
  cancelDefaultText?: string;
  cancelDictKey?: string;
  /** Element the notification is dispatched at. Defaults to the as-app in the
   *  document, which is where <as-notifications> listens. */
  app?: HTMLElement | null;
}

export interface SlowOpNotice {
  /** Disarm the timer and dismiss the notification if it is already up. Safe to
   *  call more than once, and safe to call when nothing was ever shown. */
  close(): void;
}

/**
 * Arm a slow-operation notice. Returns a handle whose `close()` must be called
 * on every path out of the operation — success, failure, and cancel alike.
 */
export function startSlowOpNotice(opts: SlowOpNoticeOptions): SlowOpNotice {
  const app = opts.app ?? asApp();
  let notificationId: string | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const close = (): void => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    if (notificationId && app) {
      sendEv(app, 'as-close-notification', {
        bubbles: false,
        detail: { id: notificationId }
      });
      notificationId = null;
    }
  };

  timer = setTimeout(() => {
    timer = null;
    if (!app) return;
    sendEv(app, 'as-notify', {
      bubbles: false,
      detail: {
        defaultText: opts.defaultText,
        dictKey: opts.dictKey,
        type: 'warning',
        persist: true,
        resolve: (id: string) => { notificationId = id; },
        actions: [
          {
            defaultText: opts.cancelDefaultText ?? 'Cancel',
            dictKey: opts.cancelDictKey ?? 'notifications.configure.timeout.cancel',
            callback: () => { close(); opts.onCancel(); }
          },
          {
            defaultText: 'Dismiss',
            dictKey: 'notifications.configure.timeout.dismiss',
            callback: () => close()
          }
        ]
      }
    });
  }, opts.delayMs);

  return { close };
}

/**
 * Read a "seconds before the cancel message" attribute off a component,
 * falling back to `fallbackSeconds` when it is absent or not a number.
 * Returns milliseconds, ready for SlowOpNoticeOptions.delayMs.
 */
export function cancelMessageDelayMs(el: HTMLElement, fallbackSeconds: number): number {
  const attr = el.getAttribute('seconds-before-cancel-message');
  const parsed = attr ? parseInt(attr, 10) : NaN;
  return (isNaN(parsed) ? fallbackSeconds : parsed) * 1000;
}
